import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { IGeoCoords, Location, Sublocation } from '@uzurv/clara';
import { isNullOrUndefined } from '@uzurv/core';

@Component({
  selector: 'app-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationComponent implements OnInit, OnChanges {
  @Input() loadedLocation: Location;
  @Input() mapLoaded: boolean;
  @Input() selectedLocation: Location;
  @Input() hoveredLocation: Location | Sublocation;
  @Input() addingNewLocation: boolean;
  @Input() newLocationName: string;
  @Input() offsetLocationMode = false;
  @Input() mapCenterCoords: IGeoCoords;
  @Input() geocodedOffsetLocation: Location;
  @Output() selectLocation = new EventEmitter<Location>();
  @Output() addNewLocation = new EventEmitter();
  @Output() saveNewLocation = new EventEmitter<string>();
  @Output() cancelNewLocation = new EventEmitter();
  @Output() locationMouseover = new EventEmitter<Location | Sublocation>();
  @Output() locationMouseout = new EventEmitter<Location | Sublocation>();
  @Output() deleteSublocation = new EventEmitter<Sublocation>();

  locationForm = new FormControl<Location | Sublocation>(null);
  newLocationNameForm = new FormControl('');

  constructor() {}

  ngOnInit() {
    this.locationForm = new FormControl(this.loadedLocation);

    this.locationForm.valueChanges.subscribe(change => {
      if (change instanceof Sublocation) {
        this.handleSelectSublocation(change);
      } else if (change instanceof Location && !isNullOrUndefined(change.id)) { // id will be null if the selection is a geocoded offset location
        this.handleSelectLocation(change);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['loadedLocation'] && !isNullOrUndefined(changes['loadedLocation'].currentValue)) {
      this.locationForm.setValue(this.loadedLocation);
    }

    if (changes['newLocationName'] && changes['newLocationName'].currentValue !== this.newLocationNameForm.value) {
      this.newLocationNameForm.setValue(changes['newLocationName'].currentValue);
    }

    if (changes['geocodedOffsetLocation'] && !isNullOrUndefined(changes['geocodedOffsetLocation'].currentValue)) {
      this.locationForm.setValue(this.geocodedOffsetLocation);
    }
  }

  shouldShowSelectedIcon(sublocation: Sublocation): boolean {
    return (
      this.selectedLocation &&
      this.selectedLocation.sublocations &&
      this.selectedLocation.sublocations[0].id === sublocation.id &&
      !(this.hoveredLocation && this.hoveredLocation.id === sublocation.id)
    );
  }

  // TODO (phill2mj): don't set sublocations as null, just emit location and optional sublocation
  handleSelectLocation(location: Location) {
    const selected = new Location();
    Object.assign(selected, this.loadedLocation);
    selected.sublocations = null;
    this.selectLocation.emit(selected);
  }

  handleSelectSublocation(sublocation: Sublocation) {
    const selected = new Location();
    Object.assign(selected, this.loadedLocation);
    selected.sublocations = [sublocation];
    this.selectLocation.emit(selected);
  }

  getFormattedLocationDisplay(display: string): { line1: string; line2: string } {
    return {
      line1: display.split(',')[0],
      line2: display
        .split(',')
        // remove what is now line 1 content
        .splice(1)
        // remove the country code
        .slice(0, -1)
        .join()
    };
  }

  formatGPSCoordinates(coordinate: IGeoCoords): string {
    return `${coordinate.lat.toFixed(6)}, ${coordinate.lng.toFixed(6)}`
  }
}
